import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Col, Label, Row} from 'reactstrap';
import {IRootState} from 'app/shared/reducers';
import {setRegistrationData} from 'app/entities/registration/registration.reducer';
import {getHealthcareFacilitiesCompact} from 'app/entities/healthcare-facility/healthcare-facility.reducer';
import VillageAvTypeahead from 'app/shared/layout/menus/village-av-typeahead';
import {IHealthcareFacility} from 'app/shared/model/healthcare-facility.model';
import 'app/entities/registration/registration.scss';
import {AvField, AvForm, AvGroup, AvInput} from 'availity-reactstrap-validation';
import {hasAnyPermission} from 'app/shared/auth/permissions';
import RegistrationButtons from './registration-buttons';
import {useRegionLabels} from 'app/shared/hooks/useRegionLabels';
import {RegionDescriptionType} from 'app/shared/model/enumerations/region-description-type.model';
import {IMMamaSettings} from 'app/shared/model/mmama-settings-model';

interface ISelectFacilityProps extends StateProps, DispatchProps {
  handlePreviousStep: () => void;
  handleNextStep: () => void;
  currentStep: number;
  countrySettings: IMMamaSettings | null;
}

const SelectFacility: React.FC<ISelectFacilityProps> = (props: ISelectFacilityProps) => {
  const {compactHealthcareFacilities, compactFacilitiesLoading, patientCase, countryId, countrySettings} = props;
  const {getLabel} = useRegionLabels();
  const [selectedHealthcareFacility, setSelectedHealthcareFacility] = useState<IHealthcareFacility | null>(null);
  const phoneMaxLength = countrySettings?.phoneNumberMaxLength || 15;

  const hfOptionList = (includeEmpty = false) => {
    const healthcareFacilityNames = compactHealthcareFacilities.map(item => ({
      short: item.name,
      council: item.councilName,
      latitude: item.locationLat,
      longitude: item.locationLon,
      district: item.districtName,
      full: item.name + ' / ' + item.councilName + ' / ' + item.districtName,
      id: item.id,
    }));

    let result = includeEmpty ? [{short: '', full: '', id: undefined, latitude: 0, longitude: 0}] : [];
    result = result.concat(healthcareFacilityNames);
    return result;
  };

  useEffect(() => {
    // reset the form
    props.setRegistrationData({
      healthcareFacilityId: null,
      villageId: null,
      patientCase: {
        ...patientCase,
        primaryContactName: '',
        primaryContactNumber: '',
      },
    });
  }, []);

  useEffect(() => {
    // Use the passed countryId from registration state
    if (countryId) {
      // Only fetch if we don't have facilities or if the existing facilities are for a different country
      if (!compactHealthcareFacilities || compactHealthcareFacilities.length === 0 || compactHealthcareFacilities[0]?.countryId !== countryId) {
        props.getHealthcareFacilitiesCompact(countryId);
      }
    }
  }, [countryId]);

  useEffect(() => {

  }, []);

  const handleFacilitySelection = (selectedFacilityId: string) => {
    const selectedFacility = compactHealthcareFacilities.find(facility => facility.id === selectedFacilityId);
    if (selectedFacility) {
      // Convert compact facility to regular facility format for compatibility
      const facilityForState = {
        id: selectedFacility.id,
        name: selectedFacility.name,
        locationLat: selectedFacility.locationLat,
        locationLon: selectedFacility.locationLon,
        level: selectedFacility.level,
        type: selectedFacility.type,
        contactName: selectedFacility.contactName,
        phoneNumber: selectedFacility.phoneNumber,
        council: {
          id: selectedFacility.councilId,
          name: selectedFacility.councilName,
          parent: {
            id: selectedFacility.districtId,
            name: selectedFacility.districtName,
          }
        }
      };
      setSelectedHealthcareFacility(facilityForState);
      props.setRegistrationData({
        healthcareFacilityId: selectedFacilityId,
        healthcareFacility: facilityForState
      });
      if (selectedFacility?.contactName || selectedFacility?.phoneNumber) {
        props.setRegistrationData({
          patientCase: {
            ...patientCase,
            primaryContactName: selectedFacility?.contactName,
            primaryContactNumber: selectedFacility?.phoneNumber,
          },
        });
      }
    }
  };

  const handleContactInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    if (selectedHealthcareFacility) {
      props.setRegistrationData({patientCase: {...patientCase, [name]: value}});
    }
  };

  return (
    <div>
      <h2 className="mb-4">Find the Pickup Facility</h2>
      <AvForm onValidSubmit={props.handleNextStep}>
        <Row>
          <Col md="12">
            <AvGroup>
              <Label for="healthcareFacility">Search for Healthcare Facility</Label>
              <VillageAvTypeahead
                labelKey="name"
                valueKey="id"
                data={hfOptionList(false)}
                placeholder={'Start Healthcare Facility'}
                id="healthcareFacility"
                onChange={handleFacilitySelection}
                loading={compactFacilitiesLoading}
                input={{
                  required: true,
                  id: 'healthcareFacility',
                  name: 'healthcareFacilityId',
                }}
              />
            </AvGroup>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <AvGroup>
              <Label for="council">{getLabel(RegionDescriptionType.COUNCIL)}</Label>
              <AvInput
                type="text"
                name="council"
                id="council"
                value={selectedHealthcareFacility?.council?.name || ''}
                readOnly
                placeholder={getLabel(RegionDescriptionType.COUNCIL)}
              />
            </AvGroup>
          </Col>
          <Col md="12">
            <AvGroup>
              <Label for="district">{getLabel(RegionDescriptionType.DISTRICT)}</Label>
              <AvInput
                type="text"
                name="district"
                id="district"
                value={selectedHealthcareFacility?.council?.parent?.name || ''}
                readOnly
                placeholder={getLabel(RegionDescriptionType.DISTRICT)}
              />
            </AvGroup>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <AvGroup>
              <Label for="facilityPhone">Facility Phone</Label>
              <AvInput
                type="text"
                name="facilityPhone"
                id="facilityPhone"
                value={selectedHealthcareFacility?.phoneNumber || ''}
                readOnly
                placeholder="Facility Phone"
              />
            </AvGroup>
          </Col>
          <Col md="12">
            <AvGroup>
              <Label for="primaryContactName">Primary Contact Name (HF in charge)</Label>
              <AvField
                type="text"
                name="primaryContactName"
                id="primaryContactName"
                value={selectedHealthcareFacility?.contactName || ''}
                onChange={handleContactInputChange}
                placeholder="Add the contact's full name"
                required
              />
            </AvGroup>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <AvGroup>
              <Label for="primaryContactNumber">Primary Contact Number</Label>
              <AvField
                type="number"
                name="primaryContactNumber"
                id="primaryContactNumber"
                value={selectedHealthcareFacility?.phoneNumber || ''}
                onChange={handleContactInputChange}
                placeholder="Add the contact's phone number"
                required
                validate={{
                  maxLength: {value: phoneMaxLength, errorMessage: `This field should be at most ${phoneMaxLength} characters long`},
                }}
              />
            </AvGroup>
          </Col>
        </Row>

        <RegistrationButtons
          currentStep={props.currentStep}
          handlePreviousStep={props.handlePreviousStep}
        />
      </AvForm>
    </div>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  compactHealthcareFacilities: storeState.healthcareFacility.compactEntities,
  compactFacilitiesLoading: storeState.healthcareFacility.compactLoading,
  userCountryId: storeState.authentication.userCountryId,
  isAdmin: hasAnyPermission(storeState.authentication.account.roles, ['ADMIN']),
  patientCase: storeState.registration.entity.patientCase,
  countryId: storeState.registration.entity.countryId,
});

const mapDispatchToProps = {
  setRegistrationData,
  getHealthcareFacilitiesCompact,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(SelectFacility);
